package nl.volleyplanner.web.controller

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.format
import kotlinx.dom.addClass
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.form
import kotlinx.html.js.onSubmitFunction
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.extensions.now
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.StaticPageController
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.util.jsObject
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.fetch.CORS
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.w3c.fetch.RequestMode
import org.w3c.xhr.FormData

class RegisterFormComponent : HTMLComponent() {
    enum class FormStatus {
        New,
        Submitting,
        Success,
        Error
    }

    private val registrationEndpoint = "https://europe-west1-volleyplanner.cloudfunctions.net/volleyplanner-register-vereniging"

    private val stateFlow = MutableStateFlow<FormStatus>(FormStatus.New)

    private fun renderNew(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.form (classes = "p-4 p-md-5 border rounded-3 bg-body-tertiary") {
            novalidate = true
            onSubmitFunction = { event ->
                event.preventDefault()
                val formElement = event.currentTarget as HTMLFormElement

                if (!formElement.checkValidity()) {
                    event.stopPropagation()
                } else {
                    val formData = FormData(formElement)

                    val email = formData.get("email") as String
                    val vereniging = formData.get("vereniging") as String
                    val functie = formData.get("functie") as String
                    val opmerkingen = formData.get("opmerkingen") as String
                    val submission_date = LocalDateTime.now().format(nl.volleyplanner.web.DATE_TIME_ISO_FORMAT)
                    val requestObject = jsObject {
                        it.email = email
                        it.vereniging = vereniging
                        it.funcie = functie
                        it.opmerkingen = opmerkingen
                        it.submission_date = submission_date
                    }

                    launchAsync {
                        stateFlow.value = FormStatus.Submitting
                        val headers = Headers()
                        headers.append("Content-Type", "application/json")
                        val response = window.fetch(
                            registrationEndpoint,
                            RequestInit(
                                method = "POST",
                                body = JSON.stringify(requestObject),
                                mode = RequestMode.CORS,
                                headers = headers
                            )
                        ).await()
                        response
                    }.onComplete { data ->
                        if (data.status == 200.toShort()) {
                            stateFlow.value = FormStatus.Success
                        } else {
                            stateFlow.value = FormStatus.Error
                        }
                    }.onError { exception ->
                        stateFlow.value = FormStatus.Error
                    }
                }

                formElement.addClass("was-validated")
            }

            div (classes = "mb-3") {
                label (classes = "form-label") {
                    htmlFor = "input-email"
                    +"Email adres"
                }
                input (classes = "form-control") {
                    id = "input-email"
                    name = "email"
                    type = InputType.email
                    required = true
                    placeholder = "naam@voorbeeld.nl"
                }
                div (classes = "invalid-feedback") {
                    +"Verplicht veld, moet een email adres bevatten"
                }
            }
            div (classes = "mb-3") {
                label (classes = "form-label") {
                    htmlFor = "input-vereniging"
                    +"Vereniging"
                }
                input (classes = "form-control") {
                    id = "input-vereniging"
                    name = "vereniging"
                    type = InputType.text
                    required = true
                    placeholder = "Jouw Vereniging"
                }
                div (classes = "invalid-feedback") {
                    +"Verplicht veld"
                }
            }
            div (classes = "mb-3") {
                label (classes = "form-label") {
                    htmlFor = "input-functie"
                    +"Jouw functie/rol"
                }
                input (classes = "form-control") {
                    id = "input-functie"
                    name = "functie"
                    type = InputType.text
                    required = true
                    placeholder = "Wedstrijdsecretaris"
                }
                div (classes = "invalid-feedback") {
                    +"Verplicht veld"
                }
            }
            div (classes = "mb-3") {
                label (classes = "form-label") {
                    htmlFor = "input-opmerkingen"
                    +"Opmerkingen"
                }
                textArea (classes = "form-control") {
                    style = "min-height: 200px;"
                    id = "input-opmerkingen"
                    name = "opmerkingen"
                    placeholder = "Indien gewenst, vertel iets over jullie specifieke beleid en " +
                            "uitdagingen tijdens de planning. Dan kan er een inschatting gemaakt " +
                            "worden of de planner in januari al aan jullie verwachtingen kan voldoen."
                }
            }
            button (classes = "w-100 btn btn-lg btn-primary") {
                type = ButtonType.submit
                +"Inschrijven"
            }
            hr (classes = "my-4") {}
            small (classes = "text-body-secondary") {
                +"Inschrijvingen worden handmatig verwerkt. Het kan een paar dagen duren voor er contact wordt opgenomen."
            }
        }
    }

    private fun renderOthers(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div (classes = "p-4 p-md-5 border rounded-3 bg-body-tertiary text-center") {
            if (stateFlow.value == FormStatus.Submitting) {
                spinner()
            } else {
                i {
                    classes += when (stateFlow.value) {
                        FormStatus.Success -> setOf("bi-check2-circle text-success")
                        FormStatus.Error -> setOf("bi-x-circle text-danger")
                        else -> throw IllegalStateException("Can not reach this state")
                    }
                    style = "font-size: 5rem;"
                }
                p {
                    if (stateFlow.value == FormStatus.Success) {
                        +"Gegevens ontvangen! We houden je de komende weken per mail op de hoogte!"
                    } else {
                        +"Er is iets misgegaan. Stuur de informatie alsjeblieft naar info@volleyplanner.nl en we maken het in orde!"
                    }
                }
            }
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return when(stateFlow.value) {
            FormStatus.New -> renderNew(consumer)
            else -> renderOthers(consumer)
        }
    }

    override suspend fun getState() = stateFlow
}

class RegisterController(override val parent: VolleyplannerWebPageIndexController) : StaticPageController() {
    override val name = "RegisterController"

    override val path = "/registreren"
    override val requiredAuthLevel = AuthLevel.AnonymousPublic
    override val title = "Inschrijven"
    override val childPages = emptyList<nl.volleyplanner.web.base.WebPageController>()

    override fun createRuntimeInstance() = RegisterController(parent)

        override suspend fun renderContent(): HTMLElement {
        return document.create.div {
            // Register info
            div(classes = "px-4 py-2") {
                div (classes = "row g-lg-5 py-5") {
                    div (classes = "col-lg-7 text-lg-start mt-2") {
                        h1 (classes = "display-4 fw-bold lh-1 text-body-emphasis mb-3 mt-5") {
                            +"Uitproberen?"
                        }
                        p (classes = "col-lg-10 fs-4") {
                            +"De planner is momenteel in de laatste fase van de ontwikkeling. Doel is om hem voor de tweede helft van de competitie begint te lanceren."
                        }
                        p (classes = "col-lg-10 fs-4") {
                            unsafe { +"Laat je gegevens hier achter en probeer de planner vrijblijvend uit. Wanneer alles gereed is nemen we contact op!" }
                        }
                    }
                    div (classes = "col-md-10 mx-auto col-lg-5") {
                        component(RegisterFormComponent())
                    }
                }
            }
        }
    }
}